/* eslint-disable complexity */
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import * as Sentry from '@sentry/react';
import {
  faAnglesUpDown,
  faExclamationCircle,
  faSpinner,
  faXmarkLarge,
} from '@fortawesome/pro-regular-svg-icons';
import {
  Button,
  Command,
  CommandGroup,
  CommandItem,
  CommandList,
  Label,
  Popover,
  PopoverContent,
  PopoverTrigger,
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@e2n/ui';
import { Termin } from '@/domain/arbeitszeiten/entities/Termin';
import { useTermine } from '@/domain/arbeitszeiten/hooks';
import { TerminItem } from '@/domain/arbeitszeiten/components/TermineSelect/TerminItem';
import dayjs from 'dayjs';
import { Arbeitszeit } from '@/domain/arbeitszeiten/entities';
import { useArbeitszeitTerminVerknuepfungUpdate } from '@/domain/arbeitszeiten/hooks/useArbeitszeitTerminUpdate';
import { toast } from '@/hooks/use-toast';
import { useManagerCanEditArbeitszeiten } from '@/domain/arbeitszeiten/hooks/useManagerCanEditArbeitszeit';
import { useStandortContext } from '@/features/standortContext/StandortContext';

// eslint-disable-next-line max-lines-per-function
export function TermineSelect({
  arbeitszeit,
  datum,
}: {
  arbeitszeit: Arbeitszeit;
  datum: string;
}) {
  const [open, setOpen] = useState(false);
  const { t } = useTranslation();
  const {
    currentStandort: { id: standortId },
  } = useStandortContext();
  const { termine, error } = useTermine(standortId, new Date(datum));
  const [isUpdating, setIsUpdating] = useState(false);
  const [selectedTermin, setSelectedTermin] = useState<Termin>(
    arbeitszeit.termin,
  );
  const [arbeitszeitTerminUpdate] = useArbeitszeitTerminVerknuepfungUpdate();
  const canEditArbeitszeit = useManagerCanEditArbeitszeiten();

  if (error) {
    return (
      <div className="flex flex-col gap-1">
        <Label>
          {t('domain.arbeitszeiten.uebersicht.termine.termineLabel')}
        </Label>
        <div className="text-neutrals-700 text-sm flex items-center gap-2">
          {t('domain.arbeitszeiten.uebersicht.termine.termineError')}
          <FontAwesomeIcon
            icon={faExclamationCircle}
            className="text-primary-red-500"
          />
        </div>
      </div>
    );
  }

  function handleSelect(newTerminId: string | null) {
    setIsUpdating(true);
    arbeitszeitTerminUpdate({
      arbeitszeitId: arbeitszeit.id,
      terminId: newTerminId,
    })
      .then((result) => {
        if (result.error) {
          toast({
            title: !newTerminId
              ? t(
                  'domain.arbeitszeiten.sheet.arbeitszeit.termin.verknuepfungRemoveError',
                )
              : t(
                  'domain.arbeitszeiten.sheet.arbeitszeit.termin.verknuepfungAddError',
                ),
          });
        } else {
          toast({
            title: !newTerminId
              ? t(
                  'domain.arbeitszeiten.sheet.arbeitszeit.termin.verknuepfungRemoveSuccess',
                )
              : t(
                  'domain.arbeitszeiten.sheet.arbeitszeit.termin.verknuepfungAddSuccess',
                ),
          });
        }
        setIsUpdating(false);

        const newSelectedTermin = termine.find((t) => t.id === newTerminId);
        setSelectedTermin(newSelectedTermin);
      })
      .catch((e) => {
        toast({
          title: t(
            'domain.arbeitszeiten.sheet.header.arbeitszeitUpdateUnknownError',
          ),
        });
        Sentry.captureException(e);
        setIsUpdating(false);
      });
    setOpen(false);
  }

  if (selectedTermin) {
    return (
      <div className="flex flex-col gap-1 w-full md:w-1/2">
        <Label>
          {t('domain.arbeitszeiten.uebersicht.termine.termineLabel')}
        </Label>
        <div className="p-3 border border-neutrals-300 flex justify-between items-center rounded-md">
          <TerminItem termin={selectedTermin} />
          {canEditArbeitszeit && (
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button
                    disabled={isUpdating}
                    onClick={() => handleSelect(null)}
                    size="icon"
                    variant="ghost">
                    {isUpdating ? (
                      <FontAwesomeIcon icon={faSpinner} spin />
                    ) : (
                      <FontAwesomeIcon icon={faXmarkLarge} />
                    )}
                  </Button>
                </TooltipTrigger>
                <TooltipContent side="bottom">
                  {t(
                    'domain.arbeitszeiten.uebersicht.termine.verknüpfungEntfernen',
                  )}
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          )}
        </div>
      </div>
    );
  }

  if (termine.length < 1) {
    return (
      <div className="flex flex-col gap-1">
        <Label>
          {t('domain.arbeitszeiten.uebersicht.termine.termineLabel')}
        </Label>
        <div className="text-neutrals-700 text-sm">
          {t('domain.arbeitszeiten.uebersicht.termine.keineTermine', {
            date: dayjs(datum).format('DD.MM.YYYY'),
          })}
        </div>
      </div>
    );
  }

  return (
    canEditArbeitszeit && (
      <div className="flex flex-col gap-1 w-full md:w-1/2">
        <Label>
          {t('domain.arbeitszeiten.uebersicht.termine.termineLabel')}
        </Label>
        <Popover open={open} onOpenChange={(open) => setOpen(open)}>
          <PopoverTrigger asChild>
            <Button
              disabled={isUpdating}
              variant="outline-secondary"
              role="combobox"
              className="justify-between h-fit">
              {!isUpdating
                ? t('domain.arbeitszeiten.uebersicht.termine.verknüpfen')
                : t(
                    'domain.arbeitszeiten.sheet.arbeitszeit.termin.verknuepfungAddLoading',
                  )}
              <FontAwesomeIcon icon={faAnglesUpDown} />
            </Button>
          </PopoverTrigger>
          <PopoverContent className="bg-white p-0">
            <Command>
              <CommandList>
                <CommandGroup className="py-2 px-3">
                  {termine.map((termin) => (
                    <CommandItem
                      className="flex items-center gap-2 px-2 py-3 m-0 rounded-md hover:cursor-pointer"
                      value={termin.id}
                      key={termin.id}
                      onSelect={handleSelect}>
                      <TerminItem termin={termin} />
                    </CommandItem>
                  ))}
                </CommandGroup>
              </CommandList>
            </Command>
          </PopoverContent>
        </Popover>
      </div>
    )
  );
}
